import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { Cancel, CheckCircle, History, Schedule } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { colors } from '../../theme';
import { Card, fmt, getDisplayText, getDisplayTone, tdSx, thSx, trSx } from './shared';

const ICONS = {
  check_circle: CheckCircle,
  cancel: Cancel,
  schedule: Schedule,
};

export default function SignalHistorySection({ signalHistory, onSelectTicker }) {
  const { t } = useTranslation();

  if (!signalHistory?.signals?.length) return null;

  return (
    <Card sx={{ mb: 2.5, px: 0 }}>
      <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <History sx={{ fontSize: 18, color: colors.gray[500] }} />
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: colors.gray[800] }}>{t('vsa.history.title')}</Typography>
          </Box>
          <Typography sx={{ fontSize: '0.65rem', color: colors.gray[400], mt: 0.3 }}>{t('vsa.history.subtitle')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          {[
            { label: t('vsa.history.winRate'), value: `${signalHistory.win_rate}%`, color: signalHistory.win_rate >= 50 ? colors.success.main : colors.error.main },
            { label: t('vsa.history.avgPnl'), value: `${signalHistory.avg_pnl >= 0 ? '+' : ''}${signalHistory.avg_pnl}%`, color: signalHistory.avg_pnl >= 0 ? colors.success.main : colors.error.main },
            { label: t('vsa.history.wins'), value: signalHistory.wins, color: colors.success.main },
            { label: t('vsa.history.losses'), value: signalHistory.losses, color: colors.error.main },
            { label: t('vsa.history.open'), value: signalHistory.open || 0, color: colors.info.main },
          ].map((stat, index) => (
            <Box key={index} sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.58rem', color: colors.gray[400], textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.03em' }}>{stat.label}</Typography>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: stat.color, fontFamily: 'monospace' }}>{stat.value}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {[
                { label: t('vsa.history.headers.date') },
                { label: t('vsa.history.headers.ticker') },
                { label: t('vsa.history.headers.setup') },
                { label: t('vsa.history.headers.signalPrice'), align: 'right' },
                { label: t('vsa.history.headers.stop'), align: 'right' },
                { label: t('vsa.history.headers.target'), align: 'right' },
                { label: t('vsa.history.headers.exitPrice'), align: 'right' },
                { label: t('vsa.history.headers.pnl'), align: 'right' },
                { label: t('vsa.history.headers.daysHeld'), align: 'center' },
                { label: t('vsa.history.headers.outcome'), align: 'center' },
              ].map((header) => <TableCell key={header.label} sx={{ ...thSx, textAlign: header.align || 'left' }}>{header.label}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {signalHistory.signals.map((signal, index) => {
              const outcome = signal.display?.outcome || {};
              const outcomeTone = getDisplayTone(outcome.tone);
              const OutcomeIcon = ICONS[outcome.icon] || Schedule;
              const pnlTone = getDisplayTone(signal.display?.pnl?.tone);
              return (
                <TableRow key={index} sx={trSx(index)}>
                  <TableCell sx={tdSx}><Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', color: colors.gray[500] }}>{signal.date}</Typography></TableCell>
                  <TableCell sx={tdSx}><Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color: colors.gray[900], cursor: 'pointer', '&:hover': { color: colors.info.main } }} onClick={() => onSelectTicker(signal.ticker)}>{signal.ticker}</Typography></TableCell>
                  <TableCell sx={tdSx}><Typography sx={{ fontSize: '0.7rem', color: colors.gray[600] }}>{getDisplayText(signal.display?.setup, t, signal.setup_type)}</Typography></TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right' }}><Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace' }}>{fmt(signal.signal_price)}</Typography></TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right' }}><Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', color: colors.error.main }}>{signal.stop_loss ? fmt(signal.stop_loss) : '—'}</Typography></TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right' }}><Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', color: colors.success.main }}>{signal.target ? fmt(signal.target) : '—'}</Typography></TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right' }}><Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', fontWeight: 700 }}>{fmt(signal.exit_price)}</Typography></TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'right' }}><Typography sx={{ fontSize: '0.72rem', fontFamily: 'monospace', fontWeight: 700, color: pnlTone.color }}>{signal.pnl_pct >= 0 ? '+' : ''}{signal.pnl_pct}%</Typography></TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'center' }}><Typography sx={{ fontSize: '0.7rem', fontFamily: 'monospace', color: colors.gray[500] }}>{signal.days_held}d</Typography></TableCell>
                  <TableCell sx={{ ...tdSx, textAlign: 'center' }}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
                      <OutcomeIcon sx={{ fontSize: 14, color: outcomeTone.color }} />
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: outcomeTone.color, whiteSpace: 'nowrap' }}>{getDisplayText(outcome, t, signal.outcome)}</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
}
